/* one% Community: the curved social wall (Community Section.dc.html).
   Drifts left at the idle speed, is pushed by page-scroll velocity, can be dragged or wheeled
   sideways, bends every card along a wide arc, and lifts the hovered card. Loops by one set width.
   Pauses while off-screen. Reduced motion: no loop, the rail is a plain horizontal scroller. */
(() => {
  if (customElements.get('op-community')) return;

  const clip = (c) => {
    c = (c || '').replace(/\s+/g, ' ').trim();
    if (c.length > 90) c = c.slice(0, 87).replace(/\S+$/, '') + '…';
    return c;
  };

  class OpCommunity extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.op-community') || this;
      this.rail = this.querySelector('[data-op-rail]');
      this.track = this.querySelector('[data-op-track]');
      this.reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      this.idle = parseFloat(this.dataset.idle);
      if (Number.isNaN(this.idle)) this.idle = 0.4;
      this.drive = parseFloat(this.dataset.drive);
      if (Number.isNaN(this.drive)) this.drive = 2.6;

      this.x = 0;
      this.vel = 0;
      this.prev = window.scrollY;
      this.held = false;
      this.hot = null;
      this.onScreen = true;
      this.cleanups = [];

      this.reveal();
      if (!this.rail || !this.track) return;

      this.listen(window, 'resize', () => this.measure(), { passive: true });
      this.measure();
      setTimeout(() => this.measure(), 50);
      this.track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', () => this.measure(), { once: true });
      });

      this.bindHover();
      if (!this.reduce) {
        this.bindDrag();
        this.watchOffscreen();
        this.start();
      }
      this.loadFeed();
    }

    disconnectedCallback() {
      this.stop();
      this.cleanups.forEach((fn) => fn());
      this.cleanups = [];
      if (this.revealIo) this.revealIo.disconnect();
      if (this.playIo) this.playIo.disconnect();
    }

    listen(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this.cleanups.push(() => target.removeEventListener(type, fn, opts));
    }

    reveal() {
      const root = this.root;
      const editor = window.Shopify && window.Shopify.designMode;
      if (this.reduce || editor || !('IntersectionObserver' in window)) {
        root.removeAttribute('data-reveal');
        return;
      }
      root.setAttribute('data-reveal', 'pending');
      this.revealIo = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        root.removeAttribute('data-reveal');
        this.revealIo.disconnect();
      }, { threshold: 0.1 });
      this.revealIo.observe(root);
    }

    measure() {
      if (!this.track) return;
      const set = this.track.querySelector('[data-ig-set]');
      this.half = set ? set.getBoundingClientRect().width : this.track.scrollWidth / 2;
      this.cards = [...this.track.querySelectorAll('[data-ig-card]')];
    }

    bindHover() {
      const rail = this.rail;
      const over = (e) => {
        const card = e.target.closest && e.target.closest('[data-ig-card]');
        this.hot = card || null;
      };
      this.listen(rail, 'pointerover', over);
      this.listen(rail, 'focusin', over);
      this.listen(rail, 'pointerleave', (e) => { if (!rail.contains(e.relatedTarget)) this.hot = null; });
      this.listen(rail, 'focusout', (e) => { if (!rail.contains(e.relatedTarget)) this.hot = null; });
      // Hovering or focusing the wall stops the idle drift; scroll still pushes it.
      this.listen(rail, 'pointerenter', () => { this.held = true; });
      this.listen(rail, 'pointerleave', () => { this.held = false; });
      this.listen(rail, 'focusin', () => { this.held = true; });
      this.listen(rail, 'focusout', (e) => { if (!rail.contains(e.relatedTarget)) this.held = false; });
    }

    bindDrag() {
      const rail = this.rail;
      this.listen(rail, 'wheel', (e) => {
        const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
        if (!dx) return;
        e.preventDefault();
        this.x -= dx;
        this.nudge = performance.now();
      }, { passive: false });
      this.listen(rail, 'pointerdown', (e) => {
        if (e.button !== 0) return;
        this.drag = { x: e.clientX, start: e.clientX, moved: false };
        rail.style.cursor = 'grabbing';
      });
      this.listen(window, 'pointermove', (e) => {
        if (!this.drag) return;
        const dx = e.clientX - this.drag.x;
        this.drag.x = e.clientX;
        this.x += dx;
        if (Math.abs(e.clientX - this.drag.start) > 6 && !this.drag.moved) {
          this.drag.moved = true;
          try { rail.setPointerCapture(e.pointerId); } catch (_) { /* not capturable */ }
        }
        this.nudge = performance.now();
      });
      const up = () => {
        if (!this.drag) return;
        this.dragged = this.drag.moved;
        this.drag = null;
        rail.style.cursor = 'grab';
        setTimeout(() => { this.dragged = false; }, 0);
      };
      this.listen(window, 'pointerup', up);
      this.listen(window, 'pointercancel', up);
      // A drag must not open the post under the pointer.
      this.listen(rail, 'click', (e) => { if (this.dragged) { e.preventDefault(); e.stopPropagation(); } }, true);
      this.listen(rail, 'dragstart', (e) => e.preventDefault());
    }

    watchOffscreen() {
      if (!('IntersectionObserver' in window)) return;
      this.playIo = new IntersectionObserver(([entry]) => {
        this.onScreen = entry.isIntersecting;
        if (this.onScreen) { this.prev = window.scrollY; this.vel = 0; this.start(); }
        else this.stop();
      });
      this.playIo.observe(this.root);
    }

    start() {
      if (this.raf || this.reduce || !this.onScreen) return;
      const loop = () => { this.frame(); this.raf = requestAnimationFrame(loop); };
      this.raf = requestAnimationFrame(loop);
    }

    stop() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    frame() {
      const cur = window.scrollY;
      this.vel = this.vel * 0.82 + (cur - this.prev) * 0.18;
      this.prev = cur;
      const track = this.track;
      const rail = this.rail;
      if (!track || !rail || !this.half) return;
      const manual = this.drag || (this.nudge && performance.now() - this.nudge < 900);
      if (!manual && !(this.idle && this.held)) this.x -= this.idle + this.vel * this.drive;
      else if (!manual) this.x -= this.vel * this.drive;
      while (this.x <= -this.half) this.x += this.half;
      while (this.x > 0) this.x -= this.half;
      track.style.transform = 'translate3d(' + this.x.toFixed(2) + 'px,0,0)';

      // Each card sits on a circle of radius R below the rail: it dips and tilts with distance from centre.
      const rb = rail.getBoundingClientRect();
      const mid = rb.width / 2;
      const R = Math.max(rb.width * 2.2, 1600);
      for (const c of this.cards || []) {
        const b = c.getBoundingClientRect();
        const x = (b.left + b.width / 2) - rb.left - mid;
        const cx = Math.max(-R * 0.98, Math.min(R * 0.98, x));
        const dip = R - Math.sqrt(R * R - cx * cx);
        const rot = Math.asin(cx / R) * 180 / Math.PI;
        const target = (this.hot === c && !this.drag) ? 1 : 0;
        c._k = (c._k || 0) + (target - (c._k || 0)) * 0.16;
        const k = c._k < 0.001 ? 0 : c._k;
        c.style.transform = 'translateY(' + (dip - k * 26).toFixed(1) + 'px) rotate(' +
          (rot * (1 - k) + k * (cx < 0 ? -1.5 : 1.5)).toFixed(2) + 'deg) scale(' + (1 + k * 0.06).toFixed(3) + ')';
        c.style.zIndex = k > 0.02 ? '2' : '';
        c.style.boxShadow = k > 0.01
          ? '0 ' + (8 + k * 26).toFixed(0) + 'px ' + (18 + k * 34).toFixed(0) + 'px rgba(26,34,34,' + (0.08 + k * 0.2).toFixed(2) + ')'
          : '';
      }
    }

    // Optional live feed. Accepts a Behold feed ({posts: [...]}, camelCase, square images under sizes)
    // or an Instagram Graph-style list ([...] or {data: [...]}). Each visitor keeps the feed for a day
    // (localStorage), so one person browsing several pages counts once against the feed's monthly views.
    loadFeed() {
      const url = this.dataset.feedUrl;
      if (!url) return;
      const count = parseInt(this.dataset.feedCount, 10) || 9;
      const key = 'op-ig-feed:' + url;
      const DAY = 24 * 60 * 60 * 1000;
      const show = (j) => {
        const raw = Array.isArray(j) ? j : j.posts || j.data || [];
        const posts = raw.map((p) => ({
          image: (p.sizes && p.sizes.medium && p.sizes.medium.mediaUrl) || p.thumbnailUrl || p.thumbnail_url
            || (p.mediaType === 'VIDEO' || p.media_type === 'VIDEO' ? '' : p.mediaUrl || p.media_url),
          permalink: p.permalink,
          caption: p.prunedCaption || p.caption || '',
        })).filter((p) => p.image).slice(0, count);
        if (posts.length >= 5 && this.isConnected) this.renderPosts(posts);
      };
      try {
        const hit = JSON.parse(localStorage.getItem(key) || 'null');
        if (hit && Date.now() - hit.t < DAY) return show(hit.j);
      } catch (e) { /* no storage: fetch every time */ }
      fetch(url, { credentials: 'omit' })
        .then((r) => { if (!r.ok) throw new Error('feed'); return r.json(); })
        .then((j) => {
          try { localStorage.setItem(key, JSON.stringify({ t: Date.now(), j })); } catch (e) {}
          show(j);
        })
        .catch(() => {});
    }

    renderPosts(posts) {
      const profile = this.dataset.profile || '';
      const openLabel = this.dataset.openLabel || '';
      const fallback = this.dataset.openFallback || '';
      const makeSet = (dup) => {
        const set = document.createElement('div');
        set.className = 'op-community__set';
        set.setAttribute('data-ig-set', '');
        if (dup) set.setAttribute('aria-hidden', 'true');
        posts.forEach((p) => {
          const a = document.createElement('a');
          a.className = 'op-community__card';
          a.setAttribute('data-ig-card', '');
          const href = p.permalink || profile;
          if (href) { a.href = href; a.target = '_blank'; a.rel = 'noopener'; }
          if (dup) a.tabIndex = -1;
          else {
            const cap = clip(p.caption);
            a.setAttribute('aria-label', cap ? cap + ' — ' + openLabel : fallback);
          }
          const img = document.createElement('img');
          img.className = 'op-community__img';
          img.src = p.image;
          img.alt = '';
          img.loading = 'lazy';
          img.draggable = false;
          img.addEventListener('load', () => this.measure(), { once: true });
          a.appendChild(img);
          set.appendChild(a);
        });
        return set;
      };
      this.track.replaceChildren(makeSet(false), makeSet(true));
      this.x = 0;
      this.hot = null;
      this.measure();
    }
  }

  customElements.define('op-community', OpCommunity);
})();
